import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SessionExpiryService {
  private static readonly REAUTH_WATCHDOG_MS = 10000;
  private reauthInProgress = false;
  private reauthWatchdogId: ReturnType<typeof setTimeout> | null = null;

  private readonly keysToClearOnReauth = [
    'bp:lastSelectedName',
    'carrier:lastSelectedApiKey',
    'carrier:lastSelectedName',
    'users:sortConfig',
    'carriers:sortConfig',
    'drivers:sortConfig',
    'metrics:selectedTimezone'
  ];

  constructor(
    private auth: AuthService,
    private router: Router
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => this.resetReauthLock());
  }

  public triggerReauthentication(): void {
    if (this.reauthInProgress) {
      return;
    }

    this.reauthInProgress = true;
    this.clearTransientStorage();
    this.startWatchdog();

    this.auth.loginWithRedirect({
      appState: { target: this.router.url || '/' }
    }).subscribe({
      // If redirect creation fails, allow retry on next auth failure.
      error: () => { this.resetReauthLock(); }
    });
  }

  private startWatchdog(): void {
    this.clearWatchdog();
    this.reauthWatchdogId = setTimeout(() => {
      this.resetReauthLock();
    }, SessionExpiryService.REAUTH_WATCHDOG_MS);
  }

  private clearWatchdog(): void {
    if (this.reauthWatchdogId == null) {
      return;
    }

    clearTimeout(this.reauthWatchdogId);
    this.reauthWatchdogId = null;
  }

  private resetReauthLock(): void {
    this.reauthInProgress = false;
    this.clearWatchdog();
  }

  private clearTransientStorage(): void {
    for (const key of this.keysToClearOnReauth) {
      try {
        localStorage.removeItem(key);
      } catch {
        // Keep reauth flow resilient if storage access is blocked.
      }
    }
  }
}
